<template>
  <div>
    <!-- Received Amounts Title and Filters -->
    <b-card
      no-body
      class="mb-0"
    >
      <b-card-header class="bg-primary text-white pt-50 pb-50">
        <h4 class="mb-0 text-white">
          Received Amounts
        </h4>
      </b-card-header>

      <!-- Error Alert -->
      <b-alert
        v-if="loadingError"
        variant="danger"
        show
        dismissible
        class="m-1 mb-0"
        @dismissed="loadingError = null"
      >
        <div class="alert-body">
          <feather-icon
            icon="AlertCircleIcon"
            class="mr-50"
          />
          <span>{{ loadingError }}</span>
        </div>
      </b-alert>

      <!-- Add Operation Error Alert (Persistent) -->
      <b-alert
        v-if="addError"
        variant="danger"
        show
        dismissible
        class="m-1 mb-0"
        @dismissed="addError = null"
      >
        <div class="alert-body">
          <feather-icon
            icon="AlertCircleIcon"
            class="mr-50"
          />
          <span>{{ addError }}</span>
        </div>
      </b-alert>

      <!-- Filters Section -->
      <b-card-body class="py-25">
        <b-row class="align-items-center">
          <!-- Date Range Filter -->
          <b-col
            cols="12"
            md="3"
            class="mb-0 mb-md-0"
          >
            <flat-picker
              v-model="dateRange"
              placeholder="Select Date Range"
              :config="{ mode: 'range' }"
              @on-close="fetchReceivedAmounts"
            />
          </b-col>

          <!-- Payment Mode Filter -->
          <b-col
            cols="12"
            md="2"
            class="mb-0 mb-md-0 mt-25 mt-md-0"
          >
            <custom-v-select
              v-model="filters.payment_mode"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="paymentModeOptions"
              :reduce="option => option.value"
              label="text"
              placeholder="Payment Mode"
              multiple
              :close-on-select="false"
              :deselect-from-dropdown="true"
              @input="fetchReceivedAmounts"
            />
          </b-col>

          <!-- Total Display -->
          <b-col
            cols="12"
            md="2"
            class="mb-0 mb-md-0 mt-25 px-0 mt-md-0"
          >
            <div class="total-received-display">
              <span class="total-label">Total:</span>
              <span class="total-amount">₹{{ totalAmount }}</span>
            </div>
          </b-col>

          <!-- Created By Filter -->
          <b-col
            cols="12"
            md="2"
            class="mb-0 mb-md-0 mt-25 mt-md-0"
          >
            <custom-v-select
              v-model="filters.created_by"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="usersList"
              label="full_name"
              placeholder="Select Created By"
              multiple
              :close-on-select="false"
              :reduce="data => data.id"
              :deselect-from-dropdown="true"
              @input="fetchReceivedAmounts"
            />
          </b-col>

          <!-- Min Amount Filter -->
          <b-col
            cols="12"
            md="1"
            style="padding-right: 1px;"
            class="mb-0 mb-md-0 mt-25 mt-md-0"
          >
            <b-form-input
              v-model.number="filters.min_amount"
              type="number"
              min="0"
              size="sm"
              placeholder="Min Amount"
              @input="debouncedFiltersChange"
            />
          </b-col>

          <!-- Max Amount Filter -->
          <b-col
            cols="12"
            md="1"
            class="mb-0 mb-md-0 px-0 mt-25 mt-md-0"
          >
            <b-form-input
              v-model.number="filters.max_amount"
              type="number"
              min="0"
              size="sm"
              placeholder="Max Amount"
              @input="debouncedFiltersChange"
            />
          </b-col>

          <!-- Clear Button -->
          <b-col
            cols="12"
            md="1"
            class="d-flex align-items-center justify-content-md-end mt-25 mt-md-0"
          >
            <b-button
              v-b-tooltip.hover
              title="Clear All Filters"
              variant="outline-secondary"
              size="sm"
              class="mb-25 mb-md-0"
              @click="clearAllFilters"
            >
              Clear
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>

      <!-- Add Received Amount Form -->
      <b-card-body class="received-form-wrapper">
        <b-row class="align-items-end">
          <b-col
            cols="12"
            md="3"
          >
            <b-form-input
              id="amount-input"
              ref="amountInput"
              v-model="newReceivedAmount.received_amount"
              type="number"
              size="sm"
              placeholder="Enter Amount"
              :disabled="isAdding"
              :class="{ 'border-danger': newReceivedAmount.received_amount !== null && newReceivedAmount.received_amount <= 0 }"
              @input="addError = null"
              @keydown.enter="focusPaymentMode"
            />
          </b-col>
          <b-col
            cols="12"
            md="3"
            class="mt-25 mt-md-0"
          >
            <v-select
              id="payment-mode-select"
              ref="paymentModeSelect"
              v-model="newReceivedAmount.payment_mode"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="paymentModeOptions"
              :reduce="option => option.value"
              :disabled="isAdding"
              label="text"
              placeholder="Payment Mode"
              class="received-select-sm"
              @input="handlePaymentModeInput"
            />
          </b-col>
          <b-col
            cols="12"
            md="4"
            class="mt-25 mt-md-0"
          >
            <b-form-input
              id="remark-input"
              ref="remarkInput"
              v-model="newReceivedAmount.remarks"
              size="sm"
              placeholder="Enter Remarks"
              :disabled="isAdding"
              @input="addError = null"
              @keydown.enter="handleRemarkEnter"
            />
          </b-col>
          <b-col
            cols="12"
            md="2"
            class="mt-25 mt-md-0"
          >
            <b-button
              ref="addButton"
              variant="primary"
              size="sm"
              block
              :disabled="!isValidNewReceivedAmount || isAdding"
              @click="addReceivedAmount"
            >
              <b-spinner
                v-if="isAdding"
                small
                class="mr-25"
              />
              <feather-icon
                v-else
                icon="PlusIcon"
                size="14"
                class="mr-25"
              />
              {{ isAdding ? 'Adding...' : 'Add' }}
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>

      <!-- Received Amounts Table -->
      <div class="table-responsive">
        <table class="table table-bordered table-sm mb-0">
          <thead>
            <tr class="bg-light">
              <th style="width: 3%;">
                #
              </th>
              <th
                style="width: 7%;"
                class="text-right"
              >
                Amount (₹)
                <div class="font-weight-bold text-success">
                  {{ formatAmount(totalReceivedAmount) }}
                </div>
              </th>
              <th style="width: 12%;">
                Payment Mode
              </th>
              <th style="width: 7%;">
                Payment Status
              </th>
              <th style="width: 5%;">
                Bill no
              </th>
              <th style="width: 18%;">
                Party
              </th>
              <th style="width: 22%;">
                Remarks
              </th>
              <th style="width: 10%;">
                Received By
              </th>
              <th style="width: 5%;">
                Actions
              </th>
            </tr>
          </thead>
          <!-- Empty State -->
          <tbody v-if="receivedAmounts.length === 0 && !loading">
            <tr>
              <td
                colspan="9"
                class="text-center text-muted py-2"
              >
                No received amounts found
              </td>
            </tr>
          </tbody>

          <!-- Loading State -->
          <tbody v-if="loading">
            <tr>
              <td
                colspan="13"
                class="text-center py-2"
              >
                <b-spinner
                  small
                  class="mr-50"
                />
                Loading bills...
              </td>
            </tr>
          </tbody>
          <tbody>
            <!-- Received Amount Items -->
            <received-amount-item
              v-for="(receivedAmount, index) in receivedAmounts"
              :key="receivedAmount.id"
              :received-amount="receivedAmount"
              :index="(currentPage - 1) * perPage + index + 1"
              :payment-mode-options="paymentModeOptions"
              :payment-status-options="paymentStatusOptions"
              @edit="startEdit"
              @updated="handleDataRefresh"
              @deleted="handleDataRefresh"
              @cancel-edit="cancelEdit"
            />
          </tbody>
        </table>
      </div>
      <detailed-pagination
        v-if="!loading && !loadingError"
        :total-items="totalItems"
        :per-page="perPage"
        :current-page="currentPage"
        @page-changed="(page) => {currentPage = page}"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BSpinner,
  BAlert,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import FlatPicker from '@/components/UI/FlatPicker.vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import getParsedQueryString from '@/utils/headerQueryParser'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import ReceivedAmountItem from './ReceivedAmountItem.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BSpinner,
    BAlert,
    FlatPicker,
    vSelect,
    CustomVSelect,
    DetailedPagination,
    ReceivedAmountItem,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [dateMixin, formatMixin],
  data() {
    return {
      // Date Range - Set today's date by default
      dateRange: `${this.formatedDate(new Date(), 'YYYY-MM-DD')} to ${this.formatedDate(new Date(), 'YYYY-MM-DD')}`,

      // Filters
      filters: {
        payment_mode: [],
        created_by: [],
        min_amount: null,
        max_amount: null,
      },

      // Data
      receivedAmounts: [],
      paymentModeOptions: [
        { value: 'cash', text: 'Cash' },
        { value: 'upi', text: 'UPI' },
        { value: 'cheque', text: 'Cheque' },
        { value: 'rgts', text: 'RGTS' },
        { value: 'credit_note', text: 'Credit Note' },
        { value: 'loan', text: 'Loan' },
        { value: 'mr_handover', text: 'MR Handover' },
        { value: 'challan_converted_bill', text: 'Challan Converted Bill' },
        { value: 'rec_by_acc_dept', text: 'Rec. By Account Dept' },
      ],
      paymentStatusOptions: [
        { value: 'paid', text: 'Paid' },
        { value: 'unpaid', text: 'Unpaid' },
        { value: 'partially_paid', text: 'Partially Paid' },
      ],
      usersList: [],

      // Loading States
      loading: false,
      isAdding: false,

      // Error State
      loadingError: null,
      addError: null,

      // Add New Received Amount - Default payment_status to 'paid'
      newReceivedAmount: {
        received_amount: null,
        payment_mode: null,
        payment_status: 'paid',
        remarks: '',
      },

      // Edit Received Amount (managed by child component)
      editingId: null,

      // Pagination
      currentPage: 1,
      perPage: 50,
      totalItems: 0,
      totalAmount: 0,

      // Debounce timer
      debounceTimer: null,
    }
  },
  computed: {
    isValidNewReceivedAmount() {
      return (
        this.newReceivedAmount.received_amount
        && this.newReceivedAmount.received_amount > 0
        && this.newReceivedAmount.payment_mode
        && this.newReceivedAmount.remarks
      )
    },
    totalReceivedAmount() {
      return this.receivedAmounts.reduce((sum, received) => sum + parseFloat(received.received_amount || 0), 0)
    },
  },
  watch: {
    currentPage() {
      this.fetchReceivedAmounts()
    },
    dateRange() {
      this.currentPage = 1
      this.fetchReceivedAmounts()
    },
  },
  mounted() {
    this.fetchUsersList()
    this.fetchReceivedAmounts()
    // Focus on amount input when component loads
    this.$nextTick(() => {
      this.focusAmountInput()
    })
  },
  activated() {
    // Focus on amount input when component is activated (for keep-alive components in tabs)
    this.$nextTick(() => {
      this.focusAmountInput()
    })
  },
  methods: {
    fetchData() {
      this.fetchReceivedAmounts()
    },

    // Debounced filter change
    debouncedFiltersChange() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      this.debounceTimer = setTimeout(() => {
        this.currentPage = 1
        this.fetchReceivedAmounts()
      }, 500)
    },

    // Focus Management Methods
    focusAmountInput() {
      if (this.$refs.amountInput) {
        this.$refs.amountInput.focus()
      }
    },

    focusPaymentMode() {
      this.$nextTick(() => {
        if (this.$refs.paymentModeSelect) {
          const selectElement = this.$refs.paymentModeSelect.$el.querySelector('input')
          if (selectElement) {
            selectElement.focus()
            // Open the dropdown
            this.$refs.paymentModeSelect.$el.querySelector('.vs__dropdown-toggle').click()
          }
        }
      })
    },

    focusRemark() {
      this.$nextTick(() => {
        if (this.$refs.remarkInput) {
          this.$refs.remarkInput.focus()
        }
      })
    },

    handlePaymentModeInput() {
      this.addError = null
      this.focusRemark()
    },

    handleRemarkEnter() {
      // If form is valid, submit; otherwise focus on the first invalid field
      if (this.isValidNewReceivedAmount && !this.isAdding) {
        this.addReceivedAmount()
      } else if (!this.newReceivedAmount.received_amount || this.newReceivedAmount.received_amount <= 0) {
        this.focusAmountInput()
      } else if (!this.newReceivedAmount.payment_mode) {
        this.focusPaymentMode()
      }
    },

    // API Calls
    async fetchUsersList() {
      try {
        const { data: users } = await axios.get('/delivery/users_list_by_group?group=operations_cashreg')
        this.usersList = users
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to fetch users. Please try again.'
      }
    },

    async fetchReceivedAmounts() {
      this.loading = true
      this.loadingError = null
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
        }

        if (this.dateRange) {
          const [startDate, endDate] = this.dateRange.split(' to ')
          if (startDate && endDate) {
            params.start_date = startDate
            params.end_date = endDate
          }
        }

        // Add filters
        if (this.filters.payment_mode && this.filters.payment_mode.length > 0) {
          params.payment_mode = this.filters.payment_mode
        }
        if (this.filters.created_by && this.filters.created_by.length > 0) {
          params.created_by = this.filters.created_by
        }
        if (this.filters.min_amount !== null && this.filters.min_amount !== '') {
          params.min_amount = this.filters.min_amount
        }
        if (this.filters.max_amount !== null && this.filters.max_amount !== '') {
          params.max_amount = this.filters.max_amount
        }

        const response = await axios.get(`/operations/received_amount/?${getParsedQueryString(params)}`)
        this.receivedAmounts = response.data.results
        this.totalItems = response.data.count
        this.totalAmount = parseFloat(response.data.total_amount.toFixed(2))
      } catch (error) {
        this.loadingError = error.response?.data?.message || 'Failed to load received amounts. Please try again.'
        this.receivedAmounts = []
        this.totalItems = 0
      } finally {
        this.loading = false
      }
    },

    async addReceivedAmount() {
      if (!this.isValidNewReceivedAmount || this.isAdding) return

      this.isAdding = true
      this.addError = null
      try {
        const formData = {
          received_amount: this.newReceivedAmount.received_amount,
          payment_mode: this.newReceivedAmount.payment_mode,
          payment_status: this.newReceivedAmount.payment_status,
          remarks: this.newReceivedAmount.remarks,
        }

        await axios.post('/operations/received_amount/', formData)

        await this.fetchReceivedAmounts()
        this.resetNewReceivedAmount()
        this.addError = null // Clear error on success

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Received amount added successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Emit event to parent to refresh data
        this.$emit('data-updated')

        // Focus back to amount input after successful submission
        this.$nextTick(() => {
          this.focusAmountInput()
        })
      } catch (error) {
        this.addError = error.response?.data?.message || 'Failed to add received amount. Please try again.'
      } finally {
        this.isAdding = false
      }
    },

    // Edit Operations
    startEdit(id) {
      this.editingId = id
    },

    cancelEdit() {
      this.editingId = null
    },

    resetNewReceivedAmount() {
      this.newReceivedAmount = {
        received_amount: null,
        payment_mode: null,
        payment_status: 'paid',
        remarks: '',
      }
    },

    // Data refresh handlers (called by child after update/delete)
    async handleDataRefresh() {
      await this.fetchReceivedAmounts()
      this.$emit('data-updated')
    },

    // Clear All Filters
    clearAllFilters() {
      this.dateRange = null
      this.filters = {
        payment_mode: [],
        created_by: [],
        min_amount: null,
        max_amount: null,
      }
      this.currentPage = 1
      this.fetchReceivedAmounts()
    },

    // Helpers
    getPaymentModeName(mode) {
      const paymentMode = this.paymentModeOptions.find(m => m.value === mode)
      return paymentMode ? paymentMode.text : mode
    },

    getPaymentStatusName(status) {
      const paymentStatus = this.paymentStatusOptions.find(s => s.value === status)
      return paymentStatus ? paymentStatus.text : status
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0'
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
/* Minimal vertical spacing utilities */
.py-25 {
  padding-top: 0.25rem !important;
  padding-bottom: 0.25rem !important;
}

.py-50 {
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
}

.pb-50 {
  padding-bottom: 0.5rem !important;
}

.mb-25 {
  margin-bottom: 0.25rem !important;
}

.mb-50 {
  margin-bottom: 0.5rem !important;
}

.mt-25 {
  margin-top: 0.25rem !important;
}

.mt-50 {
  margin-top: 0.5rem !important;
}

.mr-25 {
  margin-right: 0.25rem !important;
}

.mr-50 {
  margin-right: 0.5rem !important;
}

.mr-75 {
  margin-right: 0.75rem !important;
}

/* Form wrapper with distinct background color */
.received-form-wrapper {
  border: 2px solid #7367f0;
  border-radius: 0.5rem;
  margin: 0.5rem;
  padding: 0.5rem !important;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  box-shadow: 0 2px 4px rgba(115, 103, 240, 0.1);
}

/* Total received amount display */
.total-received-display {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(40, 199, 111, 0.85);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(40, 199, 111, 0.4);
  height: 100%;
}

.total-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  margin-right: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-amount {
  font-size: 1.125rem;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

/* Border danger for validation */
.border-danger {
  border-color: #ea5455 !important;
}

/* Compact table styling */
.table-sm td,
.table-sm th {
  padding: 0.3rem;
  vertical-align: middle;
}

.table-bordered {
  border: 1px solid #dee2e6;
}

.table-bordered td,
.table-bordered th {
  border: 1px solid #dee2e6;
}

/* Small form controls */
.form-control-sm {
  height: calc(1.5em + 0.5rem + 2px);
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

/* v-select compact styling */
.received-select-sm >>> .vs__dropdown-toggle {
  padding: 0;
  min-height: calc(1.5em + 0.5rem + 2px);
  border: 1px solid #d8d6de;
  background-color: #ffffff !important;
}

.received-select-sm >>> .vs__search {
  padding: 0.25rem 0.5rem;
  margin: 0;
  font-size: 0.875rem;
  background-color: transparent;
}

.received-select-sm >>> .vs__selected {
  margin: 0.125rem 0.25rem;
  padding: 0.125rem 0.25rem;
  font-size: 0.875rem;
  background-color: #f3f2f7;
}

.received-select-sm >>> .vs__actions {
  padding: 0 0.25rem;
}

.received-select-sm >>> .vs__dropdown-menu {
  background-color: #ffffff;
}

.received-select-sm >>> .vs__clear {
  margin-right: 0;
}

/* Badge styling */
.badge {
  padding: 0.35rem 0.5rem;
  font-size: 0.75rem;
}

/* Responsive table */
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .received-form-wrapper {
    margin: 0.25rem;
    padding: 0.5rem !important;
  }

  .justify-content-md-end {
    justify-content: flex-start !important;
  }
}
</style>
